import { Navigate, Outlet, useSearchParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import GeneralFallback from '../../components/Layout/Fallback/GeneralFallback';
import { Navbar } from '../../components/Layout/Navbar';
import { TopBar } from '../../components/Layout/TopBar';
import { MobileBottomNav } from '../../components/Layout/MobileBottomNav';
import { SwipeableContent } from '../../components/Layout/SwipeableContent';
import { FeedbackButton } from '../../components/Feedback';
import { useAuth } from '../../auth/AuthProvider';
import { WorkspaceProvider, useWorkspace } from '../../components/Workspace/artifacts';
import { useEffect } from 'react';

const Layout = () => {
  const { status } = useAuth();

  if (status === 'loading') {
    return (
      <div className="h-full flex items-center justify-center bg-neutral-50">
        <div className="text-neutral-600">Loading workspace…</div>
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/signin" replace />;
  }

  return (
    <WorkspaceProvider>
      <SearchParamOpener>
        <GeneralFallback>
          <div className="flex flex-col h-full bg-neutral-100 overflow-hidden">
            {/* Top bar with logo and search */}
            <TopBar />

            {/* Below top bar: sidebar + main content */}
            <div className="flex flex-1 min-h-0 overflow-hidden">
              {/* Sidebar navigation - hidden on mobile */}
              <Navbar />

              {/* Main content area - pages fill this entire space */}
              <div className="flex-1 min-h-0 min-w-0 flex flex-col overflow-hidden bg-white lg:bg-neutral-100">
                {/* Outlet fills available space - scrollable, with bottom padding for mobile nav */}
                {/* Swipeable wrapper only active on mobile */}
                <div className="flex-1 min-h-0 pb-16 lg:pb-0">
                  <SwipeableContent>
                    <Outlet />
                  </SwipeableContent>
                </div>
              </div>
            </div>

            {/* Mobile bottom navigation */}
            <MobileBottomNav />

            {/* Feedback button - always visible */}
            <FeedbackButton />

            <Toaster
              position="top-right"
              toastOptions={{
                duration: 3000,
                className:
                  'text-xs leading-snug px-3 py-1 rounded-lg shadow-sm bg-neutral-900/95 text-neutral-50 border border-neutral-300',
                style: {
                  fontSize: '0.75rem',
                  padding: '4px 8px',
                },
              }}
            />
          </div>
        </GeneralFallback>
      </SearchParamOpener>
    </WorkspaceProvider>
  );
};

const SearchParamOpener: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const workspace = useWorkspace();

  const normalizeArtifactId = (raw: string) =>
    raw.replace(/^artifact:/, '').replace(/:[0-9]+$/, '');

  useEffect(() => {
    const artifactId = searchParams.get('artifact');
    if (artifactId) {
      workspace.openArtifact(normalizeArtifactId(artifactId));
      // Keep line and endLine parameters for scrolling/highlighting
      // They will be read by DocumentEditor
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('artifact');
        // Keep line and endLine parameters - DocumentEditor will handle them
        return next;
      });
    }
  }, [searchParams, setSearchParams, workspace]);

  return <>{children}</>;
};

export default Layout;
